import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import IntelligencePanel from "@/components/IntelligencePanel";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { DownloadAnswerButton } from "@/components/DownloadAnswerButton";
import { downloadMainsAnswerPdf } from "@/lib/answerPdf";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import GSPYQBank from "@/components/mains/GSPYQBank";
import {
  BookOpen, Target, Award, TrendingUp, ArrowRight, Sparkles,
  FileText, BarChart3, CircleCheck, AlertCircle, Lightbulb,
  Scale, Heart, Shield, Users, Brain, Clock, CheckSquare, Lock,
  FileCheck, CheckCircle2, Camera, ArrowLeft, Search, Pencil
} from "lucide-react";

// GS-IV Diagram Components

// 1. Value Tree Diagram - Shows ethical values hierarchy
const ValueTreeDiagram = ({ answer }) => {
  const mainValue = answer.diagram?.type || "Core Ethical Value";
  const branches = answer.body?.slice(0, 5).map(b => b.heading) || ["Personal Ethics", "Professional Ethics", "Social Ethics", "Constitutional Values", "Universal Values"];
  
  return (
    <div className="border-2 border-violet-200 rounded-lg p-6 bg-gradient-to-br from-violet-50 to-purple-50">
      <h4 className="text-sm font-bold text-violet-800 mb-4 text-center">🌳 Value Tree Diagram</h4>
      <div className="flex flex-col items-center space-y-3">
        <div className="bg-violet-600 text-white rounded-lg px-6 py-3 shadow-md">
          <p className="text-sm font-bold">{mainValue}</p>
        </div>
        <div className="h-6 w-0.5 bg-violet-300"></div>
        <div className="flex items-center justify-center w-full flex-wrap gap-2">
          {branches.map((branch, idx) => (
            <div key={idx} className={`flex-1 min-w-24 max-w-32 border-2 rounded-lg px-3 py-2 text-center ${
              idx === 0 ? 'bg-blue-100 border-blue-300' :
              idx === 1 ? 'bg-purple-100 border-purple-300' :
              idx === 2 ? 'bg-pink-100 border-pink-300' :
              idx === 3 ? 'bg-amber-100 border-amber-300' :
              'bg-emerald-100 border-emerald-300'
            }`}>
              <p className="text-[10px] font-semibold">{branch}</p>
            </div>
          ))}
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 2. Ethical Dilemma Diagram - Shows conflicting values/choices
const DilemmaFlowDiagram = ({ answer }) => {
  const dilemma = answer.diagram?.type || "Ethical Dilemma";
  const options = answer.body?.slice(0, 2).map(b => b.heading) || ["Option A", "Option B"];
  const factors = answer.body?.slice(2, 5).map(b => b.heading) || ["Stakeholders", "Consequences", "Values"];
  
  return (
    <div className="border-2 border-rose-200 rounded-lg p-6 bg-gradient-to-br from-rose-50 to-pink-50">
      <h4 className="text-sm font-bold text-rose-800 mb-4 text-center">⚖️ Dilemma Flow Diagram</h4>
      <div className="flex flex-col items-center space-y-4">
        {/* Dilemma */}
        <div className="bg-rose-600 text-white rounded-lg px-6 py-3 shadow-md">
          <p className="text-sm font-bold">{dilemma}</p>
        </div>
        
        {/* Fork */}
        <div className="flex items-center gap-8">
          <div className="text-rose-400">↙</div>
          <div className="text-rose-400">↘</div>
        </div>
        
        {/* Options */}
        <div className="flex gap-8">
          <div className="bg-blue-100 border-2 border-blue-400 rounded-lg px-4 py-3 text-center w-36">
            <p className="text-xs font-bold text-blue-900">{options[0]}</p>
          </div>
          <div className="bg-orange-100 border-2 border-orange-400 rounded-lg px-4 py-3 text-center w-36">
            <p className="text-xs font-bold text-orange-900">{options[1]}</p>
          </div>
        </div>
        
        {/* Factors to consider */}
        <div className="text-xs text-slate-600 mt-2">Consider:</div>
        <div className="flex flex-wrap gap-2 justify-center">
          {factors.map((factor, idx) => (
            <span key={idx} className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-[10px] font-semibold">
              {factor}
            </span>
          ))}
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 3. Stakeholder Map Diagram - Shows stakeholders and their interests
const StakeholderMapDiagram = ({ answer }) => {
  const centerIssue = answer.diagram?.type || "Ethical Issue";
  const stakeholders = answer.body?.slice(0, 6).map(b => b.heading) || ["Government", "Citizens", "Employees", "Media", "NGOs", "Judiciary"];
  const colors = [
    'bg-blue-100 border-blue-400',
    'bg-emerald-100 border-emerald-400',
    'bg-amber-100 border-amber-400',
    'bg-rose-100 border-rose-400',
    'bg-purple-100 border-purple-400',
    'bg-cyan-100 border-cyan-400'
  ];
  
  return (
    <div className="border-2 border-cyan-200 rounded-lg p-6 bg-gradient-to-br from-cyan-50 to-teal-50">
      <h4 className="text-sm font-bold text-cyan-800 mb-4 text-center">👥 Stakeholder Map</h4>
      <div className="relative flex items-center justify-center" style={{ minHeight: '280px' }}>
        {/* Center Issue */}
        <div className="absolute z-10 bg-gradient-to-br from-cyan-600 to-teal-600 text-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
          <p className="text-[10px] font-bold text-center px-2">{centerIssue}</p>
        </div>
        
        {/* Stakeholders in circle */}
        {stakeholders.map((stakeholder, idx) => {
          const angle = (idx * 360) / stakeholders.length - 90;
          const rad = (angle * Math.PI) / 180;
          const x = Math.cos(rad) * 100;
          const y = Math.sin(rad) * 100;
          
          return (
            <React.Fragment key={idx}>
              <div
                className="absolute w-16 h-0.5 bg-cyan-300 origin-left"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${angle + 90}deg) translateY(-50%)`,
                }}
              />
              <div
                className={`absolute border-2 rounded-lg px-3 py-2 shadow-sm ${colors[idx % colors.length]}`}
                style={{
                  left: `calc(50% + ${x}px)`,
                  top: `calc(50% + ${y}px)`,
                  transform: 'translate(-50%, -50%)',
                  width: '85px'
                }}
              >
                <p className="text-[9px] font-semibold text-center">{stakeholder}</p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 4. Decision Matrix Table - Shows ethical decision factors
const DecisionMatrixDiagram = ({ answer }) => {
  const rows = answer.body?.slice(0, 5) || [];
  
  return (
    <div className="border-2 border-indigo-200 rounded-lg p-4 bg-gradient-to-br from-indigo-50 to-violet-50">
      <h4 className="text-sm font-bold text-indigo-800 mb-4 text-center">📋 Decision Matrix</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-indigo-600 text-white">
              <th className="p-2 text-left font-bold border border-indigo-700">Factor</th>
              <th className="p-2 text-left font-bold border border-indigo-700">Ethical Consideration</th>
              <th className="p-2 text-left font-bold border border-indigo-700">Impact</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-indigo-50'}>
                <td className="p-2 border border-indigo-200 font-semibold text-indigo-900">
                  {row.heading}
                </td>
                <td className="p-2 border border-indigo-200 text-slate-700">
                  {row.points?.[0]?.explanation || 'Ethical aspect'}
                </td>
                <td className="p-2 border border-indigo-200 text-slate-700">
                  {row.points?.[0]?.example || 'Outcome'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 5. Virtue Circle Diagram - Shows interconnected virtues/values
const VirtueCircleDiagram = ({ answer }) => {
  const virtues = answer.body?.slice(0, 6).map(b => b.heading) || ["Integrity", "Empathy", "Courage", "Justice", "Wisdom", "Humility"];
  const colors = [
    'bg-violet-100 border-violet-400 text-violet-900',
    'bg-pink-100 border-pink-400 text-pink-900',
    'bg-amber-100 border-amber-400 text-amber-900',
    'bg-emerald-100 border-emerald-400 text-emerald-900',
    'bg-blue-100 border-blue-400 text-blue-900',
    'bg-rose-100 border-rose-400 text-rose-900'
  ];
  
  return (
    <div className="border-2 border-purple-200 rounded-lg p-6 bg-gradient-to-br from-purple-50 to-fuchsia-50">
      <h4 className="text-sm font-bold text-purple-800 mb-4 text-center">⭕ Virtue Circle Diagram</h4>
      <div className="relative flex items-center justify-center" style={{ minHeight: '280px' }}>
        {/* Center */}
        <div className="absolute z-10 bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white rounded-full w-20 h-20 flex items-center justify-center shadow-lg">
          <p className="text-[10px] font-bold text-center">Ethical<br/>Character</p>
        </div>
        
        {/* Virtue nodes in circle */}
        {virtues.map((virtue, idx) => {
          const angle = (idx * 360) / virtues.length - 90;
          const rad = (angle * Math.PI) / 180;
          const x = Math.cos(rad) * 100;
          const y = Math.sin(rad) * 100;
          
          return (
            <div
              key={idx}
              className={`absolute border-2 rounded-lg px-3 py-2 shadow-sm ${colors[idx]}`}
              style={{
                left: `calc(50% + ${x}px)`,
                top: `calc(50% + ${y}px)`,
                transform: 'translate(-50%, -50%)',
                width: '85px'
              }}
            >
              <p className="text-[9px] font-semibold text-center">{virtue}</p>
            </div>
          );
        })}
        
        {/* Circular connection */}
        <svg className="absolute w-64 h-64 opacity-30" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="35" fill="none" stroke="#9333ea" strokeWidth="2" strokeDasharray="5,3"/>
        </svg>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// GS-IV Subjects - Ethics topics
const GS4_SUBJECTS = [
  { label: "Ethics & Human Interface", value: "ethics" },
  { label: "Attitude", value: "attitude" },
  { label: "Aptitude & Foundational Values", value: "aptitude" },
  { label: "Emotional Intelligence", value: "emotional_intelligence" },
  { label: "Public Service Values", value: "public_service_values" },
  { label: "Probity in Governance", value: "probity" },
  { label: "Case Studies", value: "case_studies" }
];

// Question types for GS-IV
const QUESTION_TYPES = [
  { label: "Theory Question", value: "theory" },
  { label: "Case Study", value: "case_study" }
];

const GS4SuitePage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState("home"); // home, answer-writing, case-study, evaluation, expected-questions, pyq-pattern, simulator
  const [loading, setLoading] = useState(false);
  
  // Answer Writing state
  const [question, setQuestion] = useState("");
  const [questionType, setQuestionType] = useState("theory");
  const [marks, setMarks] = useState(10);
  const [wordLimit, setWordLimit] = useState(150);
  const [answer, setAnswer] = useState(null);
  const [selectedDiagram, setSelectedDiagram] = useState("ValueTree");
  
  // Diagram types for GS-IV
  const diagramTypes = [
    { id: "ValueTree", label: "Value Tree", icon: "🌳", description: "Shows ethical values hierarchy" },
    { id: "DilemmaFlow", label: "Dilemma Flow", icon: "⚖️", description: "Shows ethical choices" },
    { id: "StakeholderMap", label: "Stakeholder Map", icon: "👥", description: "Maps stakeholder interests" },
    { id: "DecisionMatrix", label: "Decision Matrix", icon: "📋", description: "Ethical decision factors" },
    { id: "VirtueCircle", label: "Virtue Circle", icon: "⭕", description: "Interconnected virtues" },
  ];

  // Render diagram based on selected type
  const renderDiagram = () => {
    if (!answer) return null;
    
    switch (selectedDiagram) {
      case "DilemmaFlow":
        return <DilemmaFlowDiagram answer={answer} />;
      case "StakeholderMap":
        return <StakeholderMapDiagram answer={answer} />;
      case "DecisionMatrix":
        return <DecisionMatrixDiagram answer={answer} />;
      case "VirtueCircle":
        return <VirtueCircleDiagram answer={answer} />;
      case "ValueTree":
      default:
        return <ValueTreeDiagram answer={answer} />;
    }
  };
  
  // Case Study Simulator state
  const [simulatorActive, setSimulatorActive] = useState(false);
  const [simulatorTime, setSimulatorTime] = useState(420); // 7 minutes in seconds
  const [simulatorAnswer, setSimulatorAnswer] = useState("");
  const [simulatorSubmitted, setSimulatorSubmitted] = useState(false);
  const [simulatorEvaluation, setSimulatorEvaluation] = useState(null);
  
  // Evaluation state
  const [evalQuestion, setEvalQuestion] = useState("");
  const [evalAnswer, setEvalAnswer] = useState("");
  const [evalType, setEvalType] = useState("theory");
  const [evaluation, setEvaluation] = useState(null);
  const [evalInputMode, setEvalInputMode] = useState("type"); // "type" or "handwritten"
  
  // Trend-Based Questions state
  const [subject, setSubject] = useState("ethics");
  const [expectedData, setExpectedData] = useState(null);
  const [practicedIds, setPracticedIds] = useState([]);
  
  // PYQ Pattern state
  const [pyqSubject, setPyqSubject] = useState("ethics");
  const [pyqData, setPyqData] = useState(null);
  const [pyqYearRange, setPyqYearRange] = useState("5");
  
  // Answer usage tracking for FREE users
  const [answerUsage, setAnswerUsage] = useState({ is_pro: false, suite: "GS4", free_full_answer_used: false, answer_type: "full", message: "" });

  const isPro = user?.role === "PRO";
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!isPro) {
      toast({ description: "Saving answers as PDF is a PRO feature. Upgrade to download." });
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadMainsAnswerPdf({
        suite: "GS4", question, marks, wordLimit, answer,
        diagramElementId: "gs4-answer-diagram",
        diagramLabel: diagramTypes.find((d) => d.id === selectedDiagram)?.label,
      });
      toast({ description: "PDF downloaded." });
    } catch (e) {
      toast({ variant: "destructive", description: "Could not generate PDF." });
    } finally {
      setDownloadingPdf(false);
    }
  };
  const isFree = user?.role === "FREE";

  // Fetch answer usage on mount
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/answers/usage?suite=GS4`);
        setAnswerUsage(data);
      } catch (err) {
        console.error("Failed to fetch answer usage:", err);
      }
    };
    fetchUsage();
  }, []);

  // Simulator timer effect
  useEffect(() => {
    let interval;
    if (simulatorActive && simulatorTime > 0 && !simulatorSubmitted) {
      interval = setInterval(() => {
        setSimulatorTime((prev) => prev - 1);
      }, 1000);
    } else if (simulatorTime === 0 && !simulatorSubmitted) {
      handleSimulatorSubmit();
    }
    return () => clearInterval(interval);
  }, [simulatorActive, simulatorTime, simulatorSubmitted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Answer Writing Functions
  const handleGenerateAnswer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/answers/mains`, {
        question,
        paper: "GS4",
        marks,
        word_limit: wordLimit,
        question_type: questionType
      });
      
      // Show the answer FIRST
      setAnswer(data);
      toast({ description: `GS-IV ${questionType === 'case_study' ? 'Case Study' : 'Theory'} answer generated!` });
      
      // For FREE users: Wait 10 seconds AFTER showing answer, then update usage
      if (!isPro && data.usage) {
        setTimeout(() => {
          setAnswerUsage({
            used: data.usage.used,
            limit: data.usage.limit,
            remaining: data.usage.remaining,
            is_pro: false
          });
          toast({ 
            description: data.paywall_message || "Answer generated",
            duration: 5000
          });
        }, 10000);
      } else if (data.usage || data.quota) {
        const q = data.quota;
        setAnswerUsage({
          is_pro: true,
          suite: "GS4",
          free_full_answer_used: false,
          answer_type: "full",
          unlimited: !q,
          limit: q?.limit ?? 10,
          used: q?.used,
          remaining: q?.remaining,
          message: data.paywall_message || (q ? `${q.remaining}/${q.limit} answers left this cycle` : "Unlimited"),
        });
        if (data.paywall_message) toast({ description: data.paywall_message, duration: 6000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not generate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Simulator Functions
  const startSimulator = () => {
    setSimulatorActive(true);
    setSimulatorTime(420); // 7 minutes
    setSimulatorAnswer("");
    setSimulatorSubmitted(false);
    setSimulatorEvaluation(null);
  };

  const handleSimulatorSubmit = async () => {
    setSimulatorSubmitted(true);
    setSimulatorActive(false);
    setLoading(true);
    
    try {
      const { data } = await managedAxios.post(`${API_BASE}/eval/mains`, {
        question: question,
        answer: simulatorAnswer,
        marks: 20,
        paper: "GS4",
        question_type: "case_study",
        timed_mode: true
      });
      setSimulatorEvaluation(data);
      toast({ description: "Case study evaluated!" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not evaluate answer." });
    } finally {
      setLoading(false);
    }
  };

  // Evaluation Functions
  const handleEvaluate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/eval/mains`, {
        question: evalQuestion,
        answer: evalAnswer,
        marks: marks,
        paper: "GS4",
        question_type: evalType
      });
      setEvaluation(data);
      toast({ description: "GS-IV Evaluation generated." });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not evaluate answer." });
    } finally {
      setLoading(false);
    }
  };

  // Trend-Based Questions Functions
  const fetchExpectedQuestions = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/expected/generate`, {
        paper: "GS4",
        subject: subject,
        num_questions: 15
      });
      setExpectedData(res.data);
      setPracticedIds([]);
      toast({ description: "GS-IV Trend-Based Questions generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load trend-based questions." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "expected-questions") {
      fetchExpectedQuestions();
    }
    if (mode === "pyq-pattern") {
      fetchPyqPattern();
    }
  }, [subject, mode, pyqSubject, pyqYearRange]);

  // PYQ Pattern Functions
  const fetchPyqPattern = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/pyq/analyze`, {
        paper: "GS4",
        subject: pyqSubject,
        year_range: parseInt(pyqYearRange)
      });
      setPyqData(res.data);
      toast({ description: "GS-IV PYQ Pattern analysis generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load PYQ pattern." });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPracticed = async (questionId) => {
    try {
      await managedAxios.post(`${API_BASE}/practice/expected`, {
        question_id: questionId,
        state: "GS4",
        bucket: subject,
      });
      setPracticedIds((prev) => (prev.includes(questionId) ? prev : [...prev, questionId]));
    } catch (e) {
      // soft fail
    }
  };

  const handleSendToMains = (questionText, questionId, type = "theory") => {
    if (questionId) {
      handleMarkPracticed(questionId);
    }
    setQuestion(questionText);
    setQuestionType(type);
    setMode("answer-writing");
  };

  // CHAPTERWISE PYQ BANK VIEW
  if (mode === "pyq-bank") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30">
        <GSPYQBank
          paper="GS4"
          onBack={() => setMode("home")}
          onPractice={(q, m) => {
            setQuestion(q || "");
            if (m) setMarks(m);
            setQuestionType("theory");
            setMode("answer-writing");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }


  // HOME VIEW
  if (mode === "home") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30">
        
        {/* ═══════════════════════════════════════════════════════════
            VIBRANT HEADER - Violet/Purple Theme for GS-IV (Ethics)
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            {/* Back button & Title */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate("/mains")}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Scale className="w-7 h-7" />
                    GS-IV Suite
                  </h1>
                  <p className="text-violet-100 text-sm">Ethics • Integrity • Case Studies</p>
                </div>
              </div>
              
              {/* PRO Badge */}
              <Badge className={`px-3 py-1.5 ${isPro 
                ? 'bg-white/20 text-white border-0' 
                : 'bg-white/10 text-violet-100 border-0'}`}>
                {isPro ? '⭐ PRO' : '🆓 FREE'}
              </Badge>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? (answerUsage.unlimited ? '∞' : (answerUsage.remaining ?? answerUsage.limit ?? 10)) : answerUsage.free_full_answer_used ? '0' : '1'}</p>
                <p className="text-[10px] text-violet-200">{isPro && !answerUsage.unlimited ? 'Answers Left' : 'Full Answers'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? '7m' : '—'}</p>
                <p className="text-[10px] text-violet-200">Simulator {!isPro && '🔒'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">10pt</p>
                <p className="text-[10px] text-violet-200">Checklist</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          
          {/* ═══════════════════════════════════════════════════════════
              TOPPER TIP BANNER
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-violet-100 to-purple-100 overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">GS-IV Strategy</p>
                  <p className="text-sm text-violet-700">Value prioritization + Stakeholder analysis + Trust-building conclusion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═══════════════════════════════════════════════════════════
              MAIN FEATURES GRID
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-2 gap-4">
            {/* Answer Writing */}
            <Card 
              className="border-2 border-violet-200 bg-gradient-to-br from-violet-500/10 to-purple-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("answer-writing")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Theory & Case</h3>
                      {!isPro && (
                        <Badge className={`text-xs ${!answerUsage.free_full_answer_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {!answerUsage.free_full_answer_used ? '1 FREE' : 'Partial'}
                        </Badge>
                      )}
                      {isPro && !answerUsage.unlimited && typeof answerUsage.remaining === 'number' && (
                        <Badge className={`text-xs ${answerUsage.remaining > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {answerUsage.remaining}/{answerUsage.limit || 10} left
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Ethics theory & case study answers</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-violet-600">
                    Start Writing
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Exam Simulator - PRO Only */}
            <Card 
              className={`border-2 cursor-pointer transition-all overflow-hidden ${
                isPro 
                  ? 'border-amber-200 bg-gradient-to-br from-amber-500/10 to-orange-500/10 hover:shadow-xl hover:scale-[1.02]' 
                  : 'border-slate-200 bg-slate-50/50'
              } group`}
              onClick={() => {
                if (!isPro) {
                  navigate("/upgrade");
                  return;
                }
                setMode("simulator");
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform ${
                    isPro ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-slate-400'
                  }`}>
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Exam Simulator</h3>
                      {!isPro && <Badge className="bg-slate-200 text-slate-600">🔒 PRO</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">7-minute timed case study practice</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className={`flex items-center gap-1 text-sm font-medium ${isPro ? 'text-amber-600' : 'text-slate-400'}`}>
                    {isPro ? 'Start Simulation' : 'Upgrade to PRO'}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Smart Evaluation */}
            <Card 
              className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("evaluation")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Smart Evaluation</h3>
                    <p className="text-sm text-slate-600 mt-1">10-point handling checklist scoring</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-indigo-600">
                    Evaluate
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trend-Based Questions */}
            <Card 
              className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("expected-questions")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Trend-Based Questions</h3>
                    <p className="text-sm text-slate-600 mt-1">Predicted from 20+ years of UPSC PYQ patterns</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600">
                    Explore
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PYQ Pattern */}
            <Card 
              className="border-2 border-rose-200 bg-gradient-to-br from-rose-500/10 to-pink-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden col-span-2"
              onClick={() => setMode("pyq-pattern")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">PYQ Pattern Analysis</h3>
                    <p className="text-sm text-slate-600 mt-1">Ethics PYQ trends, patterns & forecasting</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-rose-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chapterwise PYQ Bank */}
          <Card
            className="border-2 border-violet-200 bg-gradient-to-br from-violet-500/10 to-purple-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
            onClick={() => setMode("pyq-bank")}
            data-testid="gs-pyq-open-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Chapterwise PYQ Bank</h3>
                    <Badge className="text-xs bg-green-100 text-green-700">FREE</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Every GS-IV mains question (2013-2026) mapped chapter & subtopic-wise with answer approach</p>
                </div>
                <ArrowRight className="w-5 h-5 text-violet-500" />
              </div>
            </CardContent>
          </Card>


          {/* ═══════════════════════════════════════════════════════════
              QUICK FEATURES ROW
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-5 gap-2">
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-2.5 text-center">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center mx-auto mb-1.5 shadow-md">
                  <Scale className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-[10px] text-slate-800">Values</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-2.5 text-center">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center mx-auto mb-1.5 shadow-md">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-[10px] text-slate-800">Stakeholders</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-2.5 text-center">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center mx-auto mb-1.5 shadow-md">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-[10px] text-slate-800">Law-Ethics</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-2.5 text-center">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-1.5 shadow-md">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-[10px] text-slate-800">Thinkers</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-2.5 text-center">
                <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-1.5 shadow-md">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <p className="font-medium text-[10px] text-slate-800">Trust</p>
              </CardContent>
            </Card>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              10-POINT HANDLING CHECKLIST
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border border-slate-200 bg-white">
            <CardContent className="p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                  <CheckSquare className="w-4 h-4 text-white" />
                </div>
                Handling Points Checklist
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  "1. Ethical conflict",
                  "2. Values prioritised",
                  "3. Stakeholders",
                  "4. Multiple options",
                  "5. Law-ethics balance",
                  "6. Feasible decision",
                  "7. Risks mitigated",
                  "8. Communication",
                  "9. Accountability",
                  "10. Trust conclusion"
                ].map((point, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200/50 rounded-xl p-2.5 text-xs text-slate-700 hover:border-violet-300 hover:bg-violet-100/50 transition-colors">
                    {point}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* PRO Upgrade CTA for FREE users */}
          {!isPro && (
            <Card 
              className="border-2 border-violet-300 bg-gradient-to-r from-violet-50 to-purple-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate("/upgrade")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                    <p className="text-sm text-slate-600">Unlock Exam Simulator, unlimited answers & full evaluation</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-violet-500" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Quote */}
          <div className="text-center pt-4 pb-8">
            <p className="text-sm text-slate-400 italic">&ldquo;Ethics is knowing the difference between what you have a right to do and what is right to do.&rdquo; — Potter Stewart</p>
          </div>
        </div>
      </div>
    );
  }

  // ANSWER WRITING VIEW (Theory & Case Study)
  if (mode === "answer-writing") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-IV Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">⚖️ GS-IV Answer Writing</h1>
          <p className="text-sm text-violet-50">
            Ethics · Integrity · Case Studies — Handling-aware, examiner-calibrated answers
          </p>
        </div>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <form className="space-y-6" onSubmit={handleGenerateAnswer}>
              {/* Question Type Selection */}
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">QUESTION TYPE</label>
                <div className="flex gap-3">
                  {QUESTION_TYPES.map((type) => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={questionType === type.value ? "default" : "outline"}
                      onClick={() => setQuestionType(type.value)}
                      className={questionType === type.value ? "bg-violet-500 hover:bg-violet-600" : ""}
                    >
                      {type.value === "case_study" ? "📋" : "📝"} {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">
                  {questionType === "case_study" ? "CASE STUDY" : "THEORY QUESTION"}
                </label>
                <Textarea
                  rows={questionType === "case_study" ? 8 : 4}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={questionType === "case_study" 
                    ? "Paste the full case study here with all details and stakeholders..."
                    : "Enter your GS-IV theory question (Ethics, Integrity, Aptitude)..."
                  }
                  required
                  className="text-sm resize-none border-2 focus:border-violet-400"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">MARKS</label>
                  <div className="flex gap-2">
                    {[10, 15, 20, 25].map((m) => (
                      <Button
                        key={m}
                        type="button"
                        variant={marks === m ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setMarks(m);
                          setWordLimit(questionType === "case_study" ? 250 : m * 15);
                        }}
                        className={marks === m ? "bg-violet-500 hover:bg-violet-600" : ""}
                      >
                        {m}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">WORD LIMIT</label>
                  <input
                    type="number"
                    value={wordLimit}
                    onChange={(e) => setWordLimit(parseInt(e.target.value))}
                    className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-md focus:border-violet-400 focus:outline-none"
                    min="50"
                    max="500"
                  />
                </div>
              </div>

              {/* Handling Points Reminder */}
              {questionType === "case_study" && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-violet-900 mb-2">📋 Case Study Handling Points (Auto-enforced):</p>
                  <div className="grid grid-cols-2 gap-1 text-xs text-violet-700">
                    <span>✓ Core ethical conflict</span>
                    <span>✓ Value prioritisation</span>
                    <span>✓ Stakeholder handling</span>
                    <span>✓ 3+ options with analysis</span>
                    <span>✓ Framework application</span>
                    <span>✓ Option rejection justification</span>
                    <span>✓ Implementation steps</span>
                    <span>✓ Trust-building conclusion</span>
                  </div>
                </div>
              )}

              {/* Diagram Type Selector */}
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">SELECT DIAGRAM TYPE FOR YOUR ANSWER</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {diagramTypes.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setSelectedDiagram(d.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedDiagram === d.id 
                          ? "bg-violet-500 border-violet-600 text-white shadow-md" 
                          : "bg-white border-slate-200 hover:border-violet-300 hover:bg-violet-50"
                      }`}
                    >
                      <div className="text-2xl mb-1">{d.icon}</div>
                      <div className={`text-xs font-bold ${selectedDiagram === d.id ? 'text-white' : 'text-slate-800'}`}>
                        {d.label}
                      </div>
                      <div className={`text-[9px] mt-1 ${selectedDiagram === d.id ? 'text-violet-100' : 'text-slate-500'}`}>
                        {d.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold py-3"
              >
                {loading ? "Generating GS-IV Answer..." : `🚀 Generate ${questionType === 'case_study' ? 'Case Study Solution' : 'Theory Answer'}`}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Display Answer */}
        {answer && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">Model Answer</h2>
              <DownloadAnswerButton locked={!isPro} busy={downloadingPdf} onClick={handleDownloadPdf} testid="gs4-download-pdf" />
            </div>
            {/* Question Interpretation / Conflict Identification */}
            {answer.interpretation && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                      {questionType === "case_study" ? "⚡" : "Q"}
                    </div>
                    <h3 className="text-lg font-bold text-violet-900 uppercase">
                      {questionType === "case_study" ? "Core Ethical Conflict & Value Prioritisation" : "Question Interpretation"}
                    </h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800">{answer.interpretation}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Definition (Theory) / Stakeholder Analysis (Case Study) */}
            {answer.intro && (
              <Card className="border-violet-200 bg-violet-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                    <h3 className="text-lg font-bold text-violet-900 uppercase">
                      {questionType === "case_study" ? "Stakeholder Analysis" : "Definition & Conceptual Clarity"}
                    </h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-violet-200">
                    <p className="text-sm text-slate-800 leading-relaxed">{answer.intro}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Diagram (PRO Only) */}
            {answer.diagram && isPro && (
              <Card className="border-violet-200 bg-violet-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-bold text-violet-900 uppercase">
                      Diagram: {diagramTypes.find(d => d.id === selectedDiagram)?.label}
                    </h3>
                  </div>
                  <div id="gs4-answer-diagram" className="bg-white rounded-lg">{renderDiagram()}</div>
                </CardContent>
              </Card>
            )}

            {/* Multi-Dimensional Body */}
            {answer.body && answer.body.length > 0 && (
              <Card className="border-fuchsia-200 bg-fuchsia-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-fuchsia-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                    <h3 className="text-lg font-bold text-fuchsia-900 uppercase">
                      {questionType === "case_study" ? "Options & Analysis" : "Multi-Dimensional Handling"}
                    </h3>
                  </div>

                  <div className="space-y-5">
                    {answer.body.map((section, idx) => (
                      <div key={idx} className="space-y-3">
                        <div className="bg-gradient-to-r from-violet-100 to-purple-100 rounded-lg p-4 border border-violet-200">
                          <h4 className="text-sm font-bold text-violet-900 mb-2">
                            {questionType === "case_study" ? `Option ${idx + 1}: ` : ""}{section.heading}
                          </h4>
                        </div>

                        {section.points && section.points.map((point, pidx) => (
                          <div key={pidx} className="bg-white rounded-xl border-2 border-slate-200 p-5 space-y-4">
                            <div>
                              <h5 className="text-sm font-bold text-slate-900 uppercase mb-2">{point.keyword}</h5>
                              <p className="text-sm text-slate-700 leading-relaxed">{point.explanation}</p>
                            </div>

                            {point.example && (
                              <div className="bg-indigo-50 border-l-4 border-indigo-400 rounded-r-lg p-3 flex items-start gap-2">
                                <Lightbulb className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-indigo-900 mb-1">
                                    {questionType === "case_study" ? "Ethical Merit / Risk" : "Example"}
                                  </p>
                                  <p className="text-xs text-indigo-800">{point.example}</p>
                                </div>
                              </div>
                            )}

                            {point.stat && (
                              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 flex items-start gap-2">
                                <Brain className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-amber-900 mb-1">Thinker Reference</p>
                                  <p className="text-xs text-amber-800">{point.stat}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Best Course of Action (Case Study) / Way Forward (Theory) */}
            {answer.way_forward && answer.way_forward.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                    <h3 className="text-lg font-bold text-green-900 uppercase">
                      {questionType === "case_study" ? "Best Course of Action & Implementation" : "Way Forward"}
                    </h3>
                  </div>
                  <div className="bg-white border-2 border-green-300 rounded-xl p-5">
                    <div className="space-y-3">
                      {answer.way_forward.map((item, idx) => (
                        <div key={idx} className="flex gap-3 items-start">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-green-100 text-green-900' :
                            idx < 3 ? 'bg-violet-100 text-violet-900' :
                            'bg-blue-100 text-blue-900'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-sm text-slate-800">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Examiner-Impact Conclusion */}
            {answer.conclusion && (
              <Card className="border-violet-300 bg-gradient-to-r from-violet-50 to-purple-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                    <h3 className="text-lg font-bold text-violet-900 uppercase">Examiner-Impact Conclusion</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-violet-300">
                    <p className="text-sm text-slate-800 leading-relaxed font-medium">{answer.conclusion}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Topper Notes */}
            {answer.topper_notes?.bullets && answer.topper_notes.bullets.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">6</div>
                    <h3 className="text-lg font-bold text-amber-900 uppercase">Topper Notes & Micro-Tips</h3>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-amber-200 p-5">
                    <ul className="space-y-2">
                      {answer.topper_notes.bullets.map((note, idx) => (
                        <li key={idx} className="flex gap-3 items-start">
                          <span className="flex-shrink-0 w-5 h-5 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">💡</span>
                          <span className="text-sm text-slate-800">{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Handling Checklist */}
            {answer.checklist && answer.checklist.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">7</div>
                    <h3 className="text-lg font-bold text-green-900 uppercase">
                      GS-IV Handling Points Checklist
                    </h3>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {answer.checklist.map((item, idx) => (
                      <div key={idx} className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
                        {item.present ? (
                          <CircleCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        )}
                        <span className="text-sm text-slate-800">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Score Estimate - Always out of 10 */}
            {answer.score_estimate !== undefined && (
              <Card className="border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-violet-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                        <Award className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-violet-900">Score Estimate</h3>
                        <p className="text-xs text-violet-600">Based on UPSC standards</p>
                      </div>
                    </div>
                    <div className="text-center bg-white rounded-xl px-4 py-2 border-2 border-violet-200">
                      <p className="text-3xl font-bold text-violet-900">{answer.score_estimate}/10</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Paywall Message */}
            {answer.paywall_message && (
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-300 rounded-lg p-6 text-center">
                <Scale className="w-12 h-12 text-violet-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-violet-900 mb-2">Upgrade to PRO</h3>
                <p className="text-sm text-violet-800">{answer.paywall_message}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // EXAM SIMULATOR VIEW (PRO Only)
  if (mode === "simulator" && isPro) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => { setMode("home"); setSimulatorActive(false); }}>← Back to GS-IV Suite</Button>
          <Badge className="px-4 py-2 text-sm bg-fuchsia-100 text-fuchsia-900">⏱️ EXAM MODE</Badge>
        </div>

        <div className="bg-gradient-to-r from-fuchsia-600 via-pink-600 to-rose-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-3">
            <Clock className="w-8 h-8" />
            Exam-Hall Case Study Simulator
          </h1>
          <p className="text-sm text-fuchsia-50">
            7-minute timed simulation • Stress-adaptive difficulty • Auto-evaluation
          </p>
        </div>

        {!simulatorActive && !simulatorSubmitted && (
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">CASE STUDY</label>
                <Textarea
                  rows={8}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Paste your case study here. Once you start the timer, you'll have 7 minutes to write your answer..."
                  className="text-sm resize-none border-2 focus:border-fuchsia-400"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-amber-900 mb-2">⚠️ Exam Simulation Rules:</p>
                <ul className="text-xs text-amber-800 space-y-1">
                  <li>• Time limit: 7 minutes (auto-submit on timeout)</li>
                  <li>• Word limit: ~250 words</li>
                  <li>• No hints or assistance during simulation</li>
                  <li>• Answer will be locked after submission</li>
                  <li>• Strict handling checklist evaluation</li>
                </ul>
              </div>

              <Button
                onClick={startSimulator}
                disabled={!question.trim()}
                className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white font-semibold py-3"
              >
                🚀 Start 7-Minute Simulation
              </Button>
            </CardContent>
          </Card>
        )}

        {simulatorActive && !simulatorSubmitted && (
          <Card className="border-red-200">
            <CardContent className="p-6 space-y-4">
              {/* Timer */}
              <div className={`text-center p-4 rounded-lg ${simulatorTime <= 60 ? 'bg-red-100 border-2 border-red-300' : 'bg-fuchsia-100 border-2 border-fuchsia-300'}`}>
                <p className={`text-4xl font-bold ${simulatorTime <= 60 ? 'text-red-600 animate-pulse' : 'text-fuchsia-600'}`}>
                  {formatTime(simulatorTime)}
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  {simulatorTime <= 60 ? '⚠️ Less than 1 minute remaining!' : 'Time Remaining'}
                </p>
              </div>

              {/* Case Study Display */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-700 mb-2">CASE STUDY:</p>
                <p className="text-sm text-slate-800">{question}</p>
              </div>

              {/* Answer Area */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">YOUR ANSWER:</label>
                <Textarea
                  rows={12}
                  value={simulatorAnswer}
                  onChange={(e) => setSimulatorAnswer(e.target.value)}
                  placeholder="Write your answer here. Focus on: ethical conflict, stakeholders, options, best course of action, implementation..."
                  className="text-sm resize-none border-2 focus:border-fuchsia-400"
                  autoFocus
                />
                <p className="text-xs text-slate-500 text-right">
                  Word count: ~{simulatorAnswer.split(/\s+/).filter(w => w).length} / 250
                </p>
              </div>

              <Button
                onClick={handleSimulatorSubmit}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-3"
              >
                ✅ Submit Answer
              </Button>
            </CardContent>
          </Card>
        )}

        {simulatorSubmitted && (
          <div className="space-y-6">
            {/* Submitted Answer */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">📝 Your Submitted Answer</h3>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{simulatorAnswer}</p>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Submitted in {formatTime(420 - simulatorTime)} • ~{simulatorAnswer.split(/\s+/).filter(w => w).length} words
                </p>
              </CardContent>
            </Card>

            {/* Evaluation */}
            {loading && (
              <Card className="border-fuchsia-200">
                <CardContent className="p-8 text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-fuchsia-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-600">Evaluating against handling checklist...</p>
                </CardContent>
              </Card>
            )}

            {simulatorEvaluation && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-green-900 mb-4">📊 Simulation Evaluation</h3>
                  
                  {simulatorEvaluation.score_estimate && (
                    <div className="bg-white rounded-lg p-4 border border-green-200 mb-4">
                      <p className="text-3xl font-bold text-green-900">{simulatorEvaluation.score_estimate}/20</p>
                      <p className="text-sm text-slate-600">Estimated Score</p>
                    </div>
                  )}

                  {simulatorEvaluation.intelligence && (
                    <div className="mb-4"><IntelligencePanel intelligence={simulatorEvaluation.intelligence} gaps={{ examples: simulatorEvaluation.missing_examples, keywords: simulatorEvaluation.keyword_suggestions }} /></div>
                  )}

                  {simulatorEvaluation.improved_answer && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-bold text-slate-900">✨ Topper-Level Rewrite:</h4>
                      <div className="bg-white rounded-lg p-4 border border-green-200 max-h-64 overflow-y-auto">
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{simulatorEvaluation.improved_answer}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Button
              onClick={() => {
                setSimulatorSubmitted(false);
                setSimulatorAnswer("");
                setQuestion("");
                setSimulatorEvaluation(null);
              }}
              variant="outline"
              className="w-full"
            >
              🔄 Try Another Case Study
            </Button>
          </div>
        )}
      </div>
    );
  }

  // EVALUATION VIEW
  if (mode === "evaluation") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-IV Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <Sparkles className="w-8 h-8" />
                GS-IV Smart Evaluation
              </h1>
              <p className="text-violet-50">
                10-point handling checklist • Examiner-style feedback • Topper rewrite
              </p>
            </div>
          </div>
        </div>

        {/* 10-Point Handling Checklist */}
        <Card className="border-violet-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-violet-900 mb-3">📋 Evaluation Criteria (10 Handling Points)</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                "Ethical conflict",
                "Value prioritisation",
                "Stakeholders",
                "Multiple options",
                "Law-ethics balance",
                "Feasible decision",
                "Risk mitigation",
                "Communication",
                "Accountability",
                "Trust conclusion"
              ].map((point, idx) => (
                <div key={idx} className="bg-violet-50 border border-violet-200 rounded px-2 py-1 text-xs text-violet-800 text-center">
                  {idx + 1}. {point}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="bg-violet-100 p-2 rounded-lg">
                  <FileText className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Submit Your GS-IV Answer</h2>
                  <p className="text-xs text-slate-600">Paste your answer for handling-point evaluation</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleEvaluate}>
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">QUESTION TYPE</p>
                  <div className="flex gap-2">
                    {QUESTION_TYPES.map((type) => (
                      <Button
                        key={type.value}
                        type="button"
                        size="sm"
                        variant={evalType === type.value ? "default" : "outline"}
                        onClick={() => setEvalType(type.value)}
                        className={evalType === type.value ? "bg-violet-500" : ""}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Input Mode Toggle */}
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">INPUT METHOD</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={evalInputMode === "type" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("type")}
                      className={evalInputMode === "type" ? "bg-violet-500 hover:bg-violet-600" : ""}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Type Answer
                    </Button>
                    <Button
                      type="button"
                      variant={evalInputMode === "handwritten" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("handwritten")}
                      className={evalInputMode === "handwritten" ? "bg-purple-500 hover:bg-purple-600" : ""}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Handwritten
                    </Button>
                  </div>
                </div>

                {evalInputMode === "type" ? (
                  <>
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">QUESTION</p>
                      <Textarea
                        rows={3}
                        value={evalQuestion}
                        onChange={(e) => setEvalQuestion(e.target.value)}
                        placeholder="Paste the GS-IV question or case study..."
                        required
                        className="text-sm resize-none border-2 focus:border-violet-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">YOUR ANSWER</p>
                      <Textarea
                        rows={10}
                        value={evalAnswer}
                        onChange={(e) => setEvalAnswer(e.target.value)}
                        placeholder="Paste your GS-IV answer here..."
                        required
                        className="text-sm resize-none border-2 focus:border-violet-400"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold py-3"
                    >
                      {loading ? "Evaluating..." : "🎯 Evaluate My GS-IV Answer"}
                    </Button>
                  </>
                ) : (
                  <HandwrittenUpload
                    marks={10}
                    suite="GS4"
                    onEvaluationComplete={(data) => {
                      setEvaluation(data);
                      if (data.extracted_question) setEvalQuestion(data.extracted_question);
                      if (data.extracted_answer) setEvalAnswer(data.extracted_answer);
                      toast({ description: "✅ Handwritten GS-IV answer evaluated!" });
                    }}
                    onExtractedData={(data) => {
                      if (data.question) setEvalQuestion(data.question);
                      if (data.answer) setEvalAnswer(data.answer);
                    }}
                  />
                )}
              </form>
            </CardContent>
          </Card>

          {/* Evaluation Results - Matching GS1 UI */}
          {evaluation && (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-violet-100 to-purple-100 p-2 rounded-lg">
                      <Award className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">GS-IV Evaluation Results</h2>
                      <p className="text-xs text-slate-600">Examiner-aligned Ethics & Integrity feedback</p>
                    </div>
                  </div>
                  {typeof evaluation.score_estimate === "number" && (
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-violet-500 to-purple-500 text-white px-4 py-2 rounded-xl">
                        <p className="text-xs font-semibold">Estimated Score</p>
                        <p className="text-2xl font-bold">{(evaluation.score_estimate / marks * 10).toFixed(1)}/10</p>
                      </div>
                    </div>
                  )}
                </div>

                {evaluation.intelligence && <IntelligencePanel intelligence={evaluation.intelligence} gaps={{ examples: evaluation.missing_examples, keywords: evaluation.keyword_suggestions }} />}

                {/* Detailed Scores - NEW */}
                {evaluation.scores && Object.keys(evaluation.scores).length > 0 && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">📊 Detailed Scores</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(evaluation.scores).map(([criterion, score]) => (
                          <div key={criterion} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <span className="text-xs font-medium text-slate-700 capitalize">
                              {criterion.replace(/_/g, " ")}
                            </span>
                            <Badge variant="outline" className="text-xs">{score}/10</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Strengths & Weaknesses - NEW */}
                {(evaluation.strongest_areas?.length > 0 || evaluation.weakest_areas?.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {evaluation.strongest_areas?.length > 0 && (
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-bold text-green-900">Strongest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.strongest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-green-800 flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {evaluation.weakest_areas?.length > 0 && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <p className="text-sm font-bold text-amber-900">Weakest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.weakest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-amber-800 flex items-start gap-2">
                                <span className="text-amber-600">⚠</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Structure & Handling Points */}
                {evaluation.structure_feedback?.length > 0 && (
                  <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-blue-600" />
                        <p className="text-sm font-bold text-blue-900">Handling Points Assessment</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.structure_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            {p.present !== false ? (
                              <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                            )}
                            <span><strong>{p.aspect}:</strong> {p.comment || p}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Depth & Coverage */}
                {evaluation.depth_feedback?.length > 0 && (
                  <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-violet-600" />
                        <p className="text-sm font-bold text-violet-900">Ethical Depth & Values</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.depth_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <TrendingUp className="w-3 h-3 text-violet-600 flex-shrink-0 mt-0.5" />
                            <span>{p.comment || p}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Keywords to Add */}
                {false && evaluation.keyword_suggestions?.length > 0 && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">Keywords to Add</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {evaluation.keyword_suggestions.map((keyword, i) => (
                          <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Data & Examples */}
                {false && evaluation.missing_examples?.length > 0 && (
                  <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-bold text-amber-900">Missing Data & Examples</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.missing_examples.map((example, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-amber-600 flex-shrink-0">•</span>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Elements (Detailed) - NEW */}
                {evaluation.missing_elements && (evaluation.missing_elements.keywords?.length > 0 || evaluation.missing_elements.data?.length > 0 || evaluation.missing_elements.examples?.length > 0) && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">🔍 Missing Elements</p>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {evaluation.missing_elements.keywords?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Keywords</p>
                            <div className="flex flex-wrap gap-1.5">
                              {evaluation.missing_elements.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {evaluation.missing_elements.data?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Data/Statistics</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.data.map((d, idx) => (
                                <li key={idx}>• {d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {evaluation.missing_elements.examples?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Examples</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.examples.map((ex, idx) => (
                                <li key={idx}>• {ex}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* 10-Point Improvement Plan - NEW */}
                {evaluation.improvement_plan?.length > 0 && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">🎯 10-Point Improvement Plan</p>
                      </div>
                      <ol className="space-y-2">
                        {evaluation.improvement_plan.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold">
                              {idx + 1}
                            </span>
                            <span>{point.replace(/^\d+\.\s*/, '')}</span>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                )}

                {/* Rewrite Suggestions - NEW */}
                {evaluation.rewrite_suggestions && (evaluation.rewrite_suggestions.introduction || evaluation.rewrite_suggestions.key_paragraph) && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">✏️ Rewrite Suggestions</p>
                      </div>
                      {evaluation.rewrite_suggestions.introduction && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Improved Introduction</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.introduction}
                          </p>
                        </div>
                      )}
                      {evaluation.rewrite_suggestions.key_paragraph && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Rewritten Key Paragraph</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.key_paragraph}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Better Introduction */}
                {evaluation.better_intro && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Introduction</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_intro}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Better Conclusion */}
                {evaluation.better_conclusion && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Conclusion</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_conclusion}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Improved Model Answer */}
                {evaluation.improved_answer && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">Improved Model Answer</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-y-auto text-xs text-slate-700 space-y-2">
                        {evaluation.improved_answer.split("\n").map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Meta Tips */}
                {evaluation.meta_tips?.length > 0 && (
                  <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-rose-600" />
                        <p className="text-sm font-bold text-rose-900">Meta Tips for Improvement</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.meta_tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-rose-600 flex-shrink-0 font-bold">→</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // EXPECTED QUESTIONS VIEW
  if (mode === "expected-questions") {
    // PRO-only feature check
    if (!isPro) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-IV Suite</Button>
          <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Trend-Based Questions is PRO-Only</h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Get Smart predicted questions with probability analysis, PYQ hooks, and current affairs links. Upgrade to PRO to unlock this powerful feature.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8"
                  onClick={() => window.location.href = '/upgrade'}
                >
                  Upgrade to PRO
                </Button>
                <Button variant="outline" onClick={() => setMode("home")}>
                  Back to Suite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-IV Suite</Button>
          <Badge className="px-4 py-2 text-sm bg-gradient-to-r from-amber-500 to-orange-500 text-white">⭐ PRO</Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900">🎯 GS-IV Trend-Based Questions</h1>
            <p className="text-xs text-slate-600">
              Smart predicted GS-IV questions based on past 1 year Current Affairs & PYQ patterns
            </p>
          </div>
        </div>

        {expectedData && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="flex-1 text-[11px] text-slate-700">Based on latest current affairs and GS-IV syllabus</p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-slate-600 text-[11px]">Subject:</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-800"
                >
                  {GS4_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={fetchExpectedQuestions}
                  disabled={loading}
                  className="text-[11px] rounded-full px-4 py-1.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white"
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-violet-600 border-r-transparent border-b-violet-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Loading GS-IV questions...</p>
            </CardContent>
          </Card>
        )}

        {!loading && expectedData && expectedData.questions && expectedData.questions.length > 0 && (
          <div className="space-y-3">
            {expectedData.questions.map((q, idx) => {
              const qText = typeof q === 'string' ? q : (q.question || "");
              const qId = q.id || `q-${idx}`;
              const isPracticed = practicedIds.includes(qId);

              return (
                <Card key={idx} className="border-slate-200 bg-white/90 hover:shadow-md transition-shadow">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-[11px] font-semibold text-violet-800">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        {q.probability_tag && (
                          <Badge variant="outline" className={`text-[10px] mb-2 ${q.probability_tag === '🔒 PRO' ? 'bg-slate-100 text-slate-500 border-slate-300' : 'bg-violet-50 text-violet-800 border-violet-300'}`}>
                            {q.probability_tag}
                          </Badge>
                        )}
                        {q.theme && (
                          <p className="text-[10px] text-slate-500 mb-1 font-medium">Theme: {q.theme}</p>
                        )}
                        <p className="text-sm text-slate-800 font-medium mb-2">{qText}</p>
                        
                        {q.why_likely && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-blue-900 mb-1">💡 Why this question is likely:</p>
                            <p className="text-[11px] text-blue-800">{q.why_likely}</p>
                          </div>
                        )}
                        
                        {q.pyq_hook && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-purple-900 mb-1">📚 PYQ Pattern:</p>
                            <p className="text-[11px] text-purple-800">{q.pyq_hook}</p>
                          </div>
                        )}
                        
                        {q.current_link && (
                          <div className="text-[11px] text-green-700 mb-2">
                            <span className="font-semibold">🔗 Current Link:</span> {q.current_link}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendToMains(qText, qId, questionType)}
                          className="text-[10px] px-2 py-1 h-auto"
                        >
                          Send to GS-IV
                        </Button>
                        {!isPracticed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPracticed(qId)}
                            className="text-[10px] px-2 py-1 h-auto text-green-700 hover:text-green-800 hover:bg-green-50"
                          >
                            Mark Practiced
                          </Button>
                        )}
                        {isPracticed && (
                          <span className="text-[10px] text-green-700 px-2 py-1">✓ Practiced</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // PYQ PATTERN VIEW
  if (mode === "pyq-pattern") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-IV Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <TrendingUp className="w-8 h-8" />
                GS-IV PYQ Pattern Analysis
              </h1>
              <p className="text-violet-50">
                Discover ethics question trends, case study patterns & topic forecasting
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">SELECT GS-IV TOPIC</label>
                <select
                  value={pyqSubject}
                  onChange={(e) => setPyqSubject(e.target.value)}
                  className="w-full rounded-md border-2 border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 focus:border-violet-400 focus:outline-none"
                >
                  {GS4_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">YEAR RANGE</label>
                <div className="flex gap-2">
                  {["5", "10", "15", "20"].map((years) => (
                    <Button
                      key={years}
                      type="button"
                      variant={pyqYearRange === years ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPyqYearRange(years)}
                      className={pyqYearRange === years ? "bg-violet-500 hover:bg-violet-600" : ""}
                    >
                      {years} Years
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={fetchPyqPattern}
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold"
            >
              {loading ? "Analyzing PYQ Patterns..." : "🔍 Analyze GS-IV PYQ Patterns"}
            </Button>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-violet-600 border-r-transparent border-b-violet-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Analyzing {pyqYearRange} years of GS-IV PYQ data...</p>
            </CardContent>
          </Card>
        )}

        {/* PYQ Pattern Display */}
        {!loading && pyqData && (
          <div className="space-y-6">
            {/* Summary Stats */}
            {pyqData.summary && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-violet-900">{pyqData.summary.total_questions || 0}</p>
                    <p className="text-xs text-violet-700 mt-1">Total Questions</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-900">{pyqData.summary.avg_per_year || 0}</p>
                    <p className="text-xs text-purple-700 mt-1">Avg Questions/Year</p>
                  </CardContent>
                </Card>
                <Card className="border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-pink-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-fuchsia-900">{pyqData.summary.most_frequent_topic || "N/A"}</p>
                    <p className="text-xs text-fuchsia-700 mt-1">Most Frequent Topic</p>
                  </CardContent>
                </Card>
                <Card className="border-pink-200 bg-gradient-to-br from-pink-50 to-rose-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-pink-900">{pyqData.summary.trend || "↗️"}</p>
                    <p className="text-xs text-pink-700 mt-1">Trend Direction</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Topic Frequency Analysis */}
            {pyqData.topic_frequency && pyqData.topic_frequency.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                    Topic Frequency Analysis
                  </h3>
                  <div className="space-y-3">
                    {pyqData.topic_frequency.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-violet-100 text-violet-900 rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-slate-900">{item.topic}</span>
                            <span className="text-sm font-bold text-violet-600">{item.count} Qs</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-violet-500 to-purple-500"
                              style={{ width: `${(item.count / (pyqData.summary?.total_questions || 100)) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Year-wise Trends */}
            {pyqData.year_wise && pyqData.year_wise.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-violet-600" />
                    Year-wise Question Trends
                  </h3>
                  <div className="space-y-2">
                    {pyqData.year_wise.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm font-semibold text-slate-900">{item.year}</span>
                        <Badge variant="secondary">{item.count} Questions</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actual PYQs */}
            {pyqData.questions && pyqData.questions.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-600" />
                    Previous Year Questions ({pyqData.questions.length})
                  </h3>
                  <div className="space-y-3">
                    {pyqData.questions.map((q, idx) => (
                      <Card key={idx} className="border-violet-200 bg-violet-50/50 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <Badge variant="outline" className="bg-violet-100 text-violet-900 border-violet-300">
                                {q.year}
                              </Badge>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-slate-800 font-medium mb-2">{q.question}</p>
                              {q.marks && (
                                <Badge variant="secondary" className="text-xs">{q.marks} marks</Badge>
                              )}
                              {q.type === 'case_study' && (
                                <Badge variant="secondary" className="text-xs ml-2 bg-fuchsia-100 text-fuchsia-900">
                                  Case Study
                                </Badge>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setQuestion(q.question);
                                setQuestionType(q.type || 'theory');
                                if (q.marks) setMarks(q.marks);
                                setMode("answer-writing");
                              }}
                              className="text-xs"
                            >
                              Practice
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insights */}
            {pyqData.insights && pyqData.insights.length > 0 && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-purple-600" />
                    Key Insights & Forecasting
                  </h3>
                  <ul className="space-y-2">
                    {pyqData.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-800">
                        <span className="text-purple-600 flex-shrink-0 mt-0.5">💡</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && !pyqData && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-12 text-center">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-base font-bold text-slate-900 mb-2">No PYQ Analysis Yet</p>
              <p className="text-sm text-slate-600">
                Select a GS-IV topic and year range, then click &ldquo;Analyze GS-IV PYQ Patterns&rdquo;
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return null;
};

export default GS4SuitePage;
